import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { InlineSVGModule } from 'ng-inline-svg-2';
import {
  PDFDocumentProxy,
  PDFProgressData,
  PdfViewerComponent,
  PdfViewerModule,
} from 'ng2-pdf-viewer';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DxProgressBarModule, DxTextBoxModule, DxTooltipModule } from 'devextreme-angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ng2-pdf-viewer',
  imports: [
    InlineSVGModule,
    forwardRef(() => PdfViewerModule),
    NgClass,
    FormsModule,
    DxProgressBarModule,
    DxTextBoxModule,
    TranslateModule,
    DxTooltipModule,
  ],
  templateUrl: './ng2-pdf-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Ng2PdfViewerComponent implements OnInit, OnChanges, OnDestroy {
  title = 'addatech-pdf-viewer';
  @Input() pdfSrc: any;
  @Input() hideToolBar = false;
  @Input() simpleView = false;
  @Output() afterPrintEvent = new EventEmitter<any>();

  error: any;
  page: number | null = 1;
  rotation = 0;
  zoom = 1.0;
  originalSize = true;
  pdf: any;
  renderText = true;
  isLoaded = false;
  isPrintPreparationProgress = false;
  stickToPage = false;
  progressData!: PDFProgressData;
  showAll = true;
  autoresize = true;
  fitToPage = true;
  isOutlineShown = false;
  pdfQuery = '';
  outline: any[] | undefined;
  printPdfContainer: any;

  isTouchDevice = false;
  isMobileDevice = false;
  isTabletDevice = false;

  tooltipMobilePrintWarningVisible = false;

  public subscription: Subscription = new Subscription();

  @ViewChild(PdfViewerComponent, { static: true }) private pdfComponent:
    | PdfViewerComponent
    | undefined;

  constructor(
    private ref: ChangeDetectorRef,
    public translateService: TranslateService
  ) {}

  ngOnInit(): void {
    const ua = navigator.userAgent;

    this.isMobileDevice = ua.indexOf('Mobile') !== -1;
    this.isTabletDevice = ua.indexOf('Tablet') !== -1;
    this.isTouchDevice = 'ontouchstart' in document.documentElement;
  }

  toggleOutline() {
    this.isOutlineShown = !this.isOutlineShown;
  }

  incrementPage(amount: number) {
    if ((this.page !== 1 && amount < 0) || (this.page !== this.pdf.numPages && amount > 0)) {
      if (this.page) {
        this.page += amount;
      }
    }
  }

  incrementZoom(amount: number) {
    this.zoom += amount;
  }

  rotate(angle: number) {
    this.rotation += angle;
  }

  downloadPDF() {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this.pdfSrc);
    link.setAttribute('download', 'example.pdf');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  onFileSelected(files: any) {
    const file: File = files.target.files[0];
    const reader = new FileReader();

    reader.onloadend = (e: any) => {
      this.pdfSrc = e.target.result;
    };

    reader.readAsArrayBuffer(file);
  }

  afterLoadComplete(pdf: PDFDocumentProxy) {
    this.pdf = pdf;
    this.isLoaded = true;

    this.loadOutline();
  }

  loadOutline() {
    this.pdf.getOutline().then((outline: any[]) => {
      this.outline = outline;
    });
    this.ref.markForCheck();
  }

  onError(error: any) {
    this.error = error;

    if (error.name === 'PasswordException') {
      const password = prompt('This document is password protected. Enter the password:');

      if (password) {
        this.error = null;
        this.setPassword(password);
      }
    }
  }

  setPassword(password: string) {
    let newSrc;
    if (this.pdfSrc instanceof ArrayBuffer) {
      newSrc = { data: this.pdfSrc };
    } else if (typeof this.pdfSrc === 'string') {
      newSrc = { url: this.pdfSrc };
    } else {
      newSrc = { ...this.pdfSrc };
    }
    newSrc.password = password;
    this.pdfSrc = newSrc;
  }

  onProgress(progressData: PDFProgressData) {
    if (!this.isLoaded) {
      this.progressData = progressData;
      this.isLoaded = false;
      this.error = null;
    }
  }

  navigateTo(destination: any) {
    this.pdfComponent?.pdfLinkService.pdfViewer.navigateTo(destination);
  }

  scrollToPage() {
    this.pdfComponent?.pdfViewer.scrollPageIntoView({
      pageNumber: 3,
    });
  }

  searchQueryChanged(newQuery: string) {
    if (newQuery !== this.pdfQuery) {
      this.pdfQuery = newQuery;
      this.pdfComponent?.eventBus.dispatch('find', {
        query: newQuery,
        type: 'again',
        caseSensitive: false,
        findPrevious: undefined,
        highlightAll: true,
        phraseSearch: true,
      });
    } else {
      this.pdfComponent?.eventBus.dispatch('findagain', {
        query: newQuery,
        type: 'again',
        caseSensitive: false,
        findPrevious: undefined,
        highlightAll: true,
        phraseSearch: true,
      });
    }
  }

  print(): void {
    if (this.isTouchDevice || this.isMobileDevice || this.isTabletDevice) {
      this.tooltipMobilePrintWarningVisible = true;
      return;
    }

    this.page = 1;

    if (!this.printPdfContainer) {
      this.printPdfContainer = document.createElement('iframe');
      this.printPdfContainer.classList.add('printPdfContainer');
      document.body.appendChild(this.printPdfContainer);
    }

    this.pdf.getData().then((u8: any) => {
      const blob = new Blob([u8.buffer], {
        type: 'application/pdf',
      });

      const blobUrl = window.URL.createObjectURL(blob);

      this.printPdfContainer.onload = () => {
        this.printPdfContainer.contentWindow.focus();
        this.printPdfContainer.contentWindow.print();
      };
      this.printPdfContainer.src = blobUrl;
    });
  }

  showMobilePrintWarning() {
    this.tooltipMobilePrintWarningVisible = true;
    return;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['pdfSrc'];
    if (change && change.previousValue !== change.currentValue) {
      this.page = 1;
    }
  }

  ngOnDestroy(): void {
    this.printPdfContainer?.parentNode.removeChild(this.printPdfContainer);
    this.subscription.unsubscribe();
  }
}
